import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { IMaskModule } from 'angular-imask';
import { filter, Subject, switchMap, take, takeUntil, tap, timer } from 'rxjs';
import { DialogService } from '../../services/dialog.service';
import { incrementVersion, isValidVersion } from '../../shared/utils/version.utils';
import { AppVersionsService, BulkDeployRowResult } from './app-versions.service';
import { VersionRow } from './models/version-row.model';

@Component({
    providers: [AppVersionsService],
    imports: [
        CommonModule,
        FormsModule,
        MatTableModule,
        MatButtonModule,
        MatIconModule,
        MatInputModule,
        MatFormFieldModule,
        MatProgressSpinnerModule,
        MatTooltipModule,
        IMaskModule,
    ],
    selector: 'app-app-versions',
    templateUrl: './app-versions.component.html',
    styleUrl: './app-versions.component.scss'
})
export class AppVersionsComponent implements OnInit, OnDestroy {
    protected readonly versionMask = { mask: /^\d{0,4}(\.\d{0,4}(\.\d{0,4})?)?$/ };
    public readonly displayedColumns: string[] = ['organizationName', 'id', 'appVersion', 'appVersionChangedAt', 'actions'];
    protected dataSource = new MatTableDataSource<VersionRow>([]);
    protected bulkInProgress = false;
    protected bulkResults: { success: number; failed: number } | null = null;
    protected bulkSetVersion = '';
    protected isBulkVersionValid = false;
    private readonly unsubscribe$ = new Subject<void>();

    constructor(
        private readonly appVersionsService: AppVersionsService,
        private readonly dialogService: DialogService,
        private readonly snackBar: MatSnackBar
    ) {}

    public ngOnInit(): void {
        this.loadOrganizations();
    }

    public ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    protected onEditVersionChange(row: VersionRow, value: string): void {
        row.editVersion = value;
        row.isEditVersionValid = isValidVersion(value);
    }

    protected onBulkVersionChange(value: string): void {
        this.bulkSetVersion = value;
        this.isBulkVersionValid = isValidVersion(value);
    }

    protected saveVersion(row: VersionRow): void {
        if (!row.isEditVersionValid) {
            return;
        }
        const { id, editVersion, organizationName } = row;
        this.updateRow(id, { status: 'loading' });

        this.appVersionsService
            .saveVersion(id, editVersion, organizationName)
            .pipe(take(1))
            .subscribe({
                next: () => {
                    this.markRowSuccess(id, editVersion);
                    this.scheduleRowReset(id);
                },
                error: () => {
                    this.markRowError(id);
                    this.scheduleRowReset(id);
                },
            });
    }

    protected bumpAndDeploy(row: VersionRow): void {
        const { id, appVersion, organizationName } = row;
        const newVersion = incrementVersion(appVersion);
        this.updateRow(id, { editVersion: newVersion, isEditVersionValid: true, status: 'loading' });

        this.appVersionsService
            .bumpVersion(id, newVersion, organizationName)
            .pipe(take(1))
            .subscribe({
                next: () => {
                    this.markRowSuccess(id, newVersion);
                    this.scheduleRowReset(id);
                },
                error: () => {
                    this.markRowError(id);
                    this.scheduleRowReset(id);
                },
            });
    }

    protected bumpAllAndDeploy(): void {
        const rows = this.dataSource.data;
        if (!rows.length) {
            return;
        }

        let success = 0;
        let failed = 0;

        this.dialogService
            .confirm({
                title: 'Bump All & Deploy',
                message: `This will bump the version of ${rows.length} app(s) by +0.0.1 and trigger a deployment for each. Continue?`,
                confirmText: 'Deploy All',
                cancelText: 'Cancel',
            })
            .pipe(
                filter(Boolean),
                tap(() => {
                    this.bulkInProgress = true;
                    this.bulkResults = null;
                    success = 0;
                    failed = 0;
                    this.dataSource.data = rows.map((r) => ({ ...r, status: 'loading' as const }));
                }),
                switchMap(() => {
                    const deployItems = this.dataSource.data.map((r) => ({ id: r.id, newVersion: incrementVersion(r.appVersion) }));
                    return this.appVersionsService.bulkDeploy$(deployItems);
                }),
                takeUntil(this.unsubscribe$)
            )
            .subscribe({
                next: ({ id, newVersion, outcome }: BulkDeployRowResult) => {
                    if (outcome === 'success') {
                        success++;
                        this.markRowSuccess(id, newVersion);
                        this.scheduleRowReset(id);
                    } else {
                        failed++;
                        this.markRowError(id);
                    }
                },
                complete: () => {
                    if (!this.bulkInProgress) return;
                    this.bulkInProgress = false;
                    this.bulkResults = { success, failed };
                    this.notifyBulkComplete(success, failed);
                },
            });
    }

    protected applyToAllAndDeploy(): void {
        const rows = this.dataSource.data;
        if (!rows.length || !this.isBulkVersionValid) {
            return;
        }

        const version = this.bulkSetVersion;
        let success = 0;
        let failed = 0;

        this.dialogService
            .confirm({
                title: 'Apply to All & Deploy',
                message: `This will set the version of ${rows.length} app(s) to ${version} and trigger a deployment for each. Continue?`,
                confirmText: 'Apply & Deploy',
                cancelText: 'Cancel',
            })
            .pipe(
                filter(Boolean),
                tap(() => {
                    this.bulkInProgress = true;
                    this.bulkResults = null;
                    success = 0;
                    failed = 0;
                    this.dataSource.data = rows.map((r) => ({ ...r, status: 'loading' as const }));
                }),
                switchMap(() => {
                    const deployItems = this.dataSource.data.map((r) => ({ id: r.id, newVersion: version }));
                    return this.appVersionsService.bulkDeploy$(deployItems);
                }),
                takeUntil(this.unsubscribe$)
            )
            .subscribe({
                next: ({ id, newVersion, outcome }: BulkDeployRowResult) => {
                    if (outcome === 'success') {
                        success++;
                        this.markRowSuccess(id, newVersion);
                        this.scheduleRowReset(id);
                    } else {
                        failed++;
                        this.markRowError(id);
                    }
                },
                complete: () => {
                    if (!this.bulkInProgress) return;
                    this.bulkInProgress = false;
                    this.bulkResults = { success, failed };
                    this.bulkSetVersion = '';
                    this.isBulkVersionValid = false;
                    this.notifyBulkComplete(success, failed);
                },
            });
    }

    private loadOrganizations(): void {
        this.appVersionsService
            .loadOrganizations(() => this.dataSource.data)
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((rows) => {
                this.dataSource.data = rows;
            });
    }

    private updateRow(id: string, updates: Partial<VersionRow>): void {
        this.dataSource.data = this.dataSource.data.map((r) => (r.id === id ? { ...r, ...updates } : r));
    }

    private markRowSuccess(id: string, appVersion: string): void {
        this.updateRow(id, { status: 'success', appVersion, editVersion: appVersion, isEditVersionValid: true });
    }

    private markRowError(id: string): void {
        this.updateRow(id, { status: 'error' });
    }

    private resetRowStatus(id: string): void {
        this.dataSource.data = this.dataSource.data.map((r) =>
            r.id === id && r.status !== 'loading' ? { ...r, status: 'idle' } : r
        );
    }

    private scheduleRowReset(id: string): void {
        timer(5000).pipe(takeUntil(this.unsubscribe$)).subscribe(() => this.resetRowStatus(id));
    }

    private notifyBulkComplete(success: number, failed: number): void {
        this.snackBar.open(`Bulk deploy complete: ${success} succeeded, ${failed} failed`, 'Close', {
            duration: 5000,
        });
    }
}
